import PropTypes from 'prop-types';
import { m, AnimatePresence } from 'framer-motion';
import { alpha } from '@mui/material/styles';
import { List, Stack, Button, IconButton, ListItemText, ListItem, Checkbox } from '@mui/material';
import { fData } from 'utils/formatNumber';

import Image from '../Image';
import Iconify from '../Iconify';
import { varFade } from '../animate';

const getFileData = (file) => {
  if (typeof file === 'string') {
    return {
      key: file,
    };
  }
  return {
    key: file.name,
    name: file.name,
    size: file.size,
    preview: file.preview,
  };
};

export default function MultiFilePreview({
  showPreview = false,
  showCheckbox,
  files,
  onRemove,
  onCheck,
  checkedId,
  onRemoveAll,
}) {
  const hasFile = files.length > 0;

  const URL = process.env.REACT_APP_BASE_FILES_URL;

  return (
    <>
      <List disablePadding sx={{ ...(hasFile && { my: 3 }) }}>
        <AnimatePresence>
          {files.map((file) => {
            const { key, name, size, preview } = getFileData(file);

            if (showPreview) {
              return (
                <ListItem
                  key={key}
                  component={m.div}
                  {...varFade().inRight}
                  sx={{
                    p: 0,
                    m: 0.5,
                    width: 80,
                    height: 80,
                    borderRadius: 1.25,
                    position: 'relative',
                    display: 'inline-flex',
                    border: (theme) => `solid 1px ${theme.palette.divider}`,
                  }}
                >
                  <Image
                    alt="preview"
                    sx={{
                      borderRadius: 1.25,
                    }}
                    src={typeof file === 'string' ? `${URL}/${file}` : preview}
                    ratio="1/1"
                  />
                  <IconButton
                    size="small"
                    onClick={() => onRemove(file)}
                    sx={{
                      top: 6,
                      p: '2px',
                      right: 6,
                      position: 'absolute',
                      color: 'common.white',
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                      },
                    }}
                  >
                    <Iconify icon={'eva:close-fill'} />
                  </IconButton>

                  {showCheckbox && (
                    <Checkbox
                      checked={file === checkedId}
                      onChange={(e) => onCheck({ id: file, status: e.target.checked })}
                      sx={{
                        p: '4px',
                        position: 'absolute',
                        color: 'common.white',
                        bottom: 0,
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                        },
                      }}
                    />
                  )}
                </ListItem>
              );
            }

            return (
              <ListItem
                key={key}
                component={m.div}
                {...varFade().inRight}
                sx={{
                  my: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 0.75,
                  border: (theme) => `solid 1px ${theme.palette.divider}`,
                }}
              >
                <Iconify icon={'eva:file-fill'} sx={{ width: 28, height: 28, color: 'text.secondary', mr: 2 }} />

                <ListItemText
                  primary={typeof file === 'string' ? file : name}
                  secondary={typeof file === 'string' ? '' : fData(size || 0)}
                  primaryTypographyProps={{ variant: 'subtitle2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />

                <IconButton edge="end" size="small" onClick={() => onRemove(file)}>
                  <Iconify icon={'eva:close-fill'} />
                </IconButton>
              </ListItem>
            );
          })}
        </AnimatePresence>
      </List>

      {hasFile && (
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button variant="contained" color="error" size="small" onClick={onRemoveAll}>
            Remove all images
          </Button>
        </Stack>
      )}
    </>
  );
}

MultiFilePreview.propTypes = {
  files: PropTypes.array,
  showPreview: PropTypes.bool,
  onRemove: PropTypes.func,
  onCheck: PropTypes.func,
  onRemoveAll: PropTypes.func,
  showCheckbox: PropTypes.bool,
  checkedId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
